import React, { useState } from 'react';
import { Input, Button, Popover, Divider } from 'antd';
import { BgColorsOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
  margin-bottom: 12px;
`;

const CustomPopover = styled(Popover)`
  .ant-popover-inner {
    margin-top: 0 !important;
    margin: 0 !important;
  }

  .ant-popover-inner-content {
    width: 300px !important;
    max-width: 300px !important;
    padding: 4px !important;
  }

  .ant-popover-content {
    width: 300px !important;
    max-width: 300px !important;
  }

  .ant-popover-arrow {
    display: block !important;
  }
`;

const ColorButton = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: #1890ff;
    transform: scale(1.1);
  }

  &.selected {
    border-color: #1890ff;
    box-shadow: 0 0 0 1px #1890ff;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.8);
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const CustomColorSection = styled.div`
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const CustomColorInput = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ColorPreview = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  flex-shrink: 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(45deg, #ccc 25%, transparent 25%), 
      linear-gradient(-45deg, #ccc 25%, transparent 25%), 
      linear-gradient(45deg, transparent 75%, #ccc 75%), 
      linear-gradient(-45deg, transparent 75%, #ccc 75%);
    background-size: 8px 8px;
    background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
    border-radius: 6px;
    z-index: -1;
  }
`;

const defaultColors = [
  '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF', '#FF0000', '#FF6600',
  '#FFCC00', '#CCFF00', '#66FF00', '#00FF00', '#00FF66', '#00FFCC', '#00CCFF', '#0066FF',
  '#0000FF', '#6600FF', '#CC00FF', '#FF00CC', '#FF0066', '#FF3333', '#FF6633', '#FF9933',
  '#1890FF', '#722ED1', '#13C2C2', '#52C41A', '#FAAD14', '#F5222D', '#FA8C16', '#A0D911'
];

const EnhancedColorPicker = ({ onChange, currentColor, expanded }) => {
  const [customColor, setCustomColor] = useState('#000000');
  const [selectedColor, setSelectedColor] = useState(currentColor || '#000000');
  const [visible, setVisible] = useState(false);

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    if (onChange) {
      onChange('color', color);
    }
    setVisible(false);
  };

  const handleCustomColorChange = (e) => {
    setCustomColor(e.target.value);
  };

  const applyCustomColor = () => {
    const color = customColor.trim();
    if (isValidColor(color)) {
      handleColorSelect(color);
    }
  };

  const isValidColor = (color) => {
    if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
      return true;
    }
    if (/^rgba?\([\d\s,%.]+\)$/.test(color)) {
      return true;
    }
    return false;
  };

  const content = (
    <div style={{ width: 260, padding: '4px' }}>
      <ColorGrid>
        {defaultColors.map((color) => (
          <ColorButton
            key={color}
            style={{ backgroundColor: color }}
            className={selectedColor === color ? 'selected' : ''}
            onClick={() => handleColorSelect(color)}
            title={color}
          />
        ))}
      </ColorGrid>

      <CustomColorSection>
        <div style={{ marginBottom: 8, fontSize: 12, color: '#666', fontWeight: 500 }}>
          Benutzerdefinierte Farbe:
        </div>
        <CustomColorInput>
          <Input
            size="small"
            placeholder="#FF0000 oder rgba(255,0,0,1)"
            value={customColor}
            onChange={handleCustomColorChange}
            onPressEnter={applyCustomColor}
            style={{ flex: 1, fontSize: 12 }}
          />
          <ColorPreview
            style={{
              backgroundColor: isValidColor(customColor) ? customColor : 'transparent',
              border: isValidColor(customColor) ? '1px solid #d9d9d9' : '1px dashed #ccc'
            }}
          />
          <Button
            size="small"
            type="primary"
            onClick={applyCustomColor}
            disabled={!isValidColor(customColor)}
          >
            OK
          </Button>
        </CustomColorInput>
      </CustomColorSection>
    </div>
  );

  return (
    <CustomPopover
      content={content}
      title="Text Farbe"
      trigger="click"
      placement="bottomLeft"
      visible={visible}
      onVisibleChange={setVisible}
      overlayStyle={{ zIndex: 10001 }}
      overlayClassName="enhanced-color-picker-popover"
      getPopupContainer={() => document.body}
    >
      <div className="rdw-option-wrapper" title="Text Farbe">
        <BgColorsOutlined
          style={{
            color: selectedColor !== '#000000' ? selectedColor : undefined,
          }}
        />
      </div>
    </CustomPopover>
  );
};

export default EnhancedColorPicker;
